import { useMemo } from 'react'
import { AlertCircle, CheckCircle2, FileText, Lightbulb, XCircle } from 'lucide-react'
import { useBulkScheduled } from '../../../../../../../../context/BulkScheduledContext'

const formatErrorMessage = message => {
  if (Array.isArray(message)) return message.join(' - ')
  return message || 'Error desconocido al crear la programacion.'
}

export default function Paso3ReporteProgramados() {
  const { results, documents } = useBulkScheduled()

  const reportRows = useMemo(() => {
    if (results.length > 0) return results

    return documents.map((doc, index) => ({
      index,
      doc,
      status: 'success',
      message: 'Programacion creada exitosamente'
    }))
  }, [results, documents])

  const summary = useMemo(() => {
    const total = reportRows.length
    const success = reportRows.filter(r => r.status === 'success').length
    const failed = reportRows.filter(r => r.status === 'error').length

    return { total, success, failed }
  }, [reportRows])

  const styles = {
    table: 'w-full table-fixed border-collapse font-sans text-xs leading-tight',
    th: 'py-2.5 px-1 sm:px-2 w-auto text-center break-words border-b border-gray-200 uppercase text-[0.6rem] font-bold text-gray-500',
    tr: 'border-b border-gray-50 hover:bg-gray-50/50 transition-colors',
    td: 'px-1 py-3 align-middle sm:px-2 md:px-2.5 xl:px-3 text-center',
    boldText: 'font-bold text-[0.66rem] text-slate-900',
    text: 'text-[0.64rem] font-medium text-slate-500'
  }

  return (
    <div className='flex flex-col flex-1 min-h-0 bg-white p-4 animate-in fade-in duration-500 overflow-hidden'>
      <div className='grid grid-cols-1 md:grid-cols-3 gap-6 mb-3'>
        <div className='group relative bg-slate-50 border border-slate-200 rounded-2xl p-3 overflow-hidden transition-all hover:shadow-lg hover:border-slate-300'>
          <div className='relative z-10'>
            <p className='text-[11px] font-bold uppercase tracking-wider text-slate-500'>Total procesadas</p>
            <p className='text-4xl font-black text-slate-800 mt-1'>{summary.total}</p>
          </div>
          <FileText className='absolute right-[-10px] bottom-[-10px] text-slate-200 opacity-50 transition-transform group-hover:scale-110 group-hover:rotate-6 duration-500' size={100} />
        </div>

        <div className='group relative bg-emerald-50 border border-emerald-100 rounded-2xl p-3 overflow-hidden transition-all hover:shadow-lg hover:border-emerald-200'>
          <div className='relative z-10'>
            <p className='text-[11px] font-bold uppercase tracking-wider text-emerald-600'>Creadas con exito</p>
            <p className='text-4xl font-black text-emerald-700 mt-1'>{summary.success}</p>
          </div>
          <CheckCircle2 className='absolute right-[-10px] bottom-[-10px] text-emerald-200 opacity-50 transition-transform group-hover:scale-110 group-hover:rotate-6 duration-500' size={100} />
        </div>

        <div className='group relative bg-rose-50 border border-rose-100 rounded-2xl p-3 overflow-hidden transition-all hover:shadow-lg hover:border-rose-200'>
          <div className='relative z-10'>
            <p className='text-[11px] font-bold uppercase tracking-wider text-rose-600'>Rechazadas o fallidas</p>
            <p className='text-4xl font-black text-rose-700 mt-1'>{summary.failed}</p>
          </div>
          <XCircle className='absolute right-[-10px] bottom-[-10px] text-rose-200 opacity-50 transition-transform group-hover:scale-110 group-hover:rotate-6 duration-500' size={100} />
        </div>
      </div>

      <div className='flex-1 min-h-full flex flex-col'>
        <div className='mb-3'>
          <h3 className='font-bold text-slate-900 text-xl flex items-center gap-2.5'>
            {summary.failed > 0 ? (
              <AlertCircle className='text-rose-500' size={24} />
            ) : (
              <CheckCircle2 className='text-emerald-500' size={24} />
            )}
            Detalle de programaciones
          </h3>
          <p className='text-slate-500 text-sm mt-1 ml-8 font-medium'>
            Revisa el resultado de cada registro procesado desde el archivo Excel.
          </p>
        </div>

        <div className='overflow-hidden border border-slate-200 rounded-2xl shadow-sm flex-1 min-h-[200px] flex flex-col'>
          <div className='overflow-auto flex-1 custom-scrollbar'>
            <table className={styles.table}>
              <thead className='bg-slate-50/80 backdrop-blur-sm border-b border-slate-200'>
                <tr>
                  <th className={`${styles.th} w-[10%] text-slate-600`}>Fila</th>
                  <th className={`${styles.th} w-[18%] text-slate-600`}>RUT</th>
                  <th className={`${styles.th} w-[30%] text-slate-600`}>Razon Social</th>
                  <th className={`${styles.th} w-[17%] text-slate-600`}>Estado</th>
                  <th className={`${styles.th} w-[25%] text-slate-600`}>Detalle</th>
                </tr>
              </thead>
              <tbody className='bg-white'>
                {reportRows.map(res => {
                  const isSuccess = res.status === 'success'

                  return (
                    <tr key={`${res.index}-${res.status}`} className={styles.tr}>
                      <td className={styles.td}>
                        <span className='inline-flex items-center justify-center h-6 w-8 bg-slate-100 rounded text-[10px] font-bold text-slate-500'>
                          {res.index + 1}
                        </span>
                      </td>
                      <td className={styles.td}>
                        <span className={styles.boldText}>{res.doc.rut_receptor || '-'}</span>
                      </td>
                      <td className={styles.td}>
                        <span className={`${styles.text} truncate block max-w-[200px] mx-auto`}>
                          {res.doc.business_name || 'Sin razon social'}
                        </span>
                      </td>
                      <td className={styles.td}>
                        <span className={`inline-flex items-center justify-center text-[0.62rem] font-semibold px-3 py-1.5 rounded-full border shadow-sm ${isSuccess ? 'text-emerald-700 bg-emerald-50 border-emerald-100' : 'text-rose-600 bg-rose-50 border-rose-100'}`}>
                          {isSuccess ? 'Creada' : 'Fallida'}
                        </span>
                      </td>
                      <td className={styles.td}>
                        <span className={`inline-flex items-center justify-center text-[0.62rem] font-semibold px-3 py-1.5 rounded-full border shadow-sm ${isSuccess ? 'text-emerald-700 bg-emerald-50 border-emerald-100' : 'text-rose-600 bg-rose-50 border-rose-100'}`}>
                          {isSuccess ? 'Programacion creada exitosamente' : formatErrorMessage(res.message)}
                        </span>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        </div>

        {summary.failed > 0 ? (
          <div className='mt-4 flex items-start gap-4 bg-slate-50 border border-slate-200 rounded-2xl p-3'>
            <div className='flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary shadow-sm'>
              <Lightbulb size={20} />
            </div>
            <div>
              <h4 className='font-bold text-slate-900 text-sm'>Proximo paso</h4>
              <p className='text-slate-600 text-[13px] leading-relaxed'>
                Corrige las programaciones rechazadas en tu archivo Excel y vuelve a intentar la carga solo con esos registros.
              </p>
            </div>
          </div>
        ) : (
          <div className='mt-4 flex items-start gap-4 bg-emerald-50/70 border border-emerald-100 rounded-2xl p-3'>
            <div className='flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-emerald-100 text-emerald-700 shadow-sm'>
              <CheckCircle2 size={20} />
            </div>
            <div>
              <h4 className='font-bold text-slate-900 text-sm'>Programaciones creadas</h4>
              <p className='text-slate-600 text-[13px] leading-relaxed'>
                Todos los documentos del lote quedaron activos y listos para su emision programada.
              </p>
            </div>
          </div>
        )}
      </div>

      <style>{`
        .custom-scrollbar::-webkit-scrollbar {
          width: 5px;
          height: 5px;
        }
        .custom-scrollbar::-webkit-scrollbar-track {
          background: #f8fafc;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: #e2e8f0;
          border-radius: 10px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background: #cbd5e1;
        }
      `}</style>
    </div>
  )
}
